import { css } from "@emotion/react";
import type { ExactTheme } from "src/lib/emotion/theme.ts";
import { placeholder } from "src/lib/emotion/placeholder.ts";
import type { ResizeValues } from "./TextArea.types.ts";
import grabberIcon from "./grabber.svg";

export const rootContainer = css({
  display: "flex",
  flexDirection: "column",
  minWidth: 60,
  height: "fit-content",
});

export const placeholdersContainerUnderLabel = css({
  marginTop: "5px",
  marginBottom: "10px",
});

export const textareaStyles =
  ({
    error,
    resize,
    disabled,
  }: {
    error?: string;
    resize?: ResizeValues;
    disabled?: boolean;
  }) =>
  (theme: ExactTheme) => {
    const {
      borderRadiusV2,
      colorsV2,
      textAreaV2,
      inputColorV2,
      inputWeightV2,
    } = theme;

    return css({
      position: "relative",
      width: "100%",
      minHeight: 35,
      height: 70,
      ...textAreaV2,
      color: inputColorV2.normal,
      fontWeight: inputWeightV2.normal,
      borderRadius: borderRadiusV2.primary,
      background: colorsV2.white,
      willChange: "border, box-shadow",
      transition: "border 0.15s, box-shadow 0.3s",

      resize,

      "::-webkit-resizer": {
        backgroundImage: `url(${grabberIcon})`,
        backgroundSize: "contain",
      },

      ...outline({
        theme,
        disabled,
        hasError: !!error,
      }),

      ...placeholder(theme.inputV2Placeholder),
      ":disabled": {
        color: inputColorV2.disabled,
        fontWeight: inputWeightV2.disabled,
        ...placeholder(theme.inputV2PlaceholderDisabled),
      },

      ...(disabled && {
        background: colorsV2.grey[50],
      }),
    });
  };

const outline = ({
  hasError,
  theme,
  disabled = false,
}: {
  hasError: boolean;
  disabled?: boolean;
  theme: ExactTheme;
}) => {
  if (disabled) {
    return {
      border: theme.inputBorderV2.solid.disabled.default,
    };
  }
  return {
    border: hasError
      ? theme.inputBorderV2.solid.standard.error
      : theme.inputBorderV2.solid.standard.default,
    boxShadow: "none",

    ["&:hover"]: {
      border: hasError
        ? theme.inputBorderV2.solid.hover.error
        : theme.inputBorderV2.solid.hover.default,
    },
    ["&:focus, &:focus-within"]: {
      border: hasError
        ? theme.inputBorderV2.solid.focused.error
        : theme.inputBorderV2.solid.focused.default,
      outline: "none",
      boxShadow: hasError
        ? `0 0 0 1px ${theme.colorsV2.red[500]}`
        : `0 0 0 1px ${theme.colorsV2.blue[500]}`,
    },
  };
};

export const helperTextStyles = (theme: ExactTheme) =>
  css({
    height: 18,
    margin: 0,
    textAlign: "right",
    alignContent: "end",
    fontWeight: theme.fontWeights.normal,
    fontSize: theme.fontSizesV2.body2,
    color: theme.colorsV2.grey[600],
  });
